import * as React from 'react'

import { Slot } from '@radix-ui/react-slot'

import { CommonSnipperDot } from '@/icons'
import { cn } from '@/lib/utils'

import { cva, VariantProps } from 'class-variance-authority'

export const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 items-center whitespace-nowrap rounded-sm transition-colors focus-visible:outline-none focus-visible:ring-[1px] md:ring-[0.5px] [@media(max-resolution:1.9dppx)]:ring-[1px] focus-visible:ring-ring disabled:pointer-events-none disabled:bg-white-8 truncate text-size-14 ring-[1px] md:ring-[0.5px] [@media(max-resolution:1.9dppx)]:ring-[1px] ring-transparent transition-all duration-300 cursor:pointer relative overflow-hidden before:absolute before:inset-0 before:bg-white-4 before:opacity-0 before:transition-opacity before:duration-300 before:pointer-events-none hover:before:opacity-100 disabled:cursor-not-allowed focus-visible:ring-0',
  {
    variants: {
      variant: {
        default:
          'bg-main ring-[1px] md:ring-[0.5px] [@media(max-resolution:1.9dppx)]:ring-[1px] ring-main-100 hover:ring-main-100 dark:hover:ring-white-80  hover:shadow-button disabled:ring-transparent',
        secondary:
          'ring-[1px] md:ring-[0.5px] [@media(max-resolution:1.9dppx)]:ring-[1px] ring-white-10 hover:ring-white-100',
        destructive:
          'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90 hover:ring-[1px] md:ring-[0.5px] [@media(max-resolution:1.9dppx)]:ring-[1px] hover:ring-white-10',
        icon: 'bg-transparent ring-[1px] md:ring-[0.5px] [@media(max-resolution:1.9dppx)]:ring-[1px] ring-white-10 hover:ring-white-100',
      },
      size: {
        default: 'h-10 px-3',
        sm: 'h-8 px-3',
        lg: 'w-full py-3 text-size-16',
      },
    },

    compoundVariants: [
      {
        variant: 'default',
      },
      {
        size: 'default',
        className: 'py-3 text-black disabled:text-white-48',
      },
      {
        size: 'sm',
        className:
          'disabled:bg-black-12 disabled:ring-[1px] md:ring-[0.5px] [@media(max-resolution:1.9dppx)]:ring-[1px] disabled:ring-white-10 disabled:text-white-48 text-black-100',
      },
      {
        size: 'sm',
        variant: 'default',
        className: 'dark:text-black-100 text-white-100',
      },
      {
        size: 'default',
        variant: 'default',
        className: 'dark:text-black-100 text-white-100 dark:disabled:text-white-48',
      },
      {
        size: 'lg',
        variant: 'default',
        className: 'dark:text-black-100 disabled:text-white-48 dark:disabled:text-white-48',
      },
      {
        variant: 'secondary',
        size: ['default', 'lg'],
        className: 'text-white-100 disabled:text-white-48 disabled:ring-transparent',
      },
      {
        variant: 'secondary',
        size: 'sm',
        className: 'text-white-100 disabled:text-white-48 hover:ring-white-100',
      },
      {
        variant: 'icon',
        className: 'text-white-100 disabled:text-white-48 disabled:bg-dark-background p-0',
      },
      {
        variant: 'icon',
        size: 'default',
        className: 'w-10 h-10',
      },
      {
        variant: 'icon',
        size: 'sm',
        className: 'px-1 py-1 hover:ring-white-100',
      },
      {
        variant: 'icon',
        size: 'lg',
        className: 'w-14 h-14',
      },
    ],
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  isLoading?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, type = 'button', children, isLoading, disabled, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    return (
      <Comp
        className={cn(
          {
            'pointer-events-none cursor-not-allowed': isLoading || disabled,
          },
          buttonVariants({ variant, size, className })
        )}
        ref={ref}
        disabled={disabled}
        {...props}
        type={type}
      >
        <>
          {isLoading && <CommonSnipperDot className='size-4 animate-spin' />}
          {children}
        </>
      </Comp>
    )
  }
)
Button.displayName = 'Button'

const ButtonPrimary = ({ children, className, ...props }: ButtonProps) => {
  return (
    <Button {...props} className={cn('duration-300 capitalize', className)}>
      {children}
    </Button>
  )
}

export { Button, ButtonPrimary }
